import { useMemo } from 'react';
import {
  SettingsRegular,
  AccessibilityRegular,
  ColorRegular,
  ShieldRegular,
  InfoRegular,
  BeakerRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import type { FluentIcon } from '@fluentui/react-icons';
import type { SettingsModalSection } from '@/hooks/useSettingsModal';
import { CopilotLogoIcon } from '@/components/common/CopilotIcon';

export interface SettingsSectionDescriptor {
  id: SettingsModalSection;
  label: string;
  /** Optional override for the modal's H2 heading. Falls back to `label`
   *  when absent — used when the page title should diverge from the nav
   *  item (e.g. nav says "Auto Portal" but the page reads "About Auto"). */
  title?: string;
  icon: FluentIcon;
  crumb: string;
  /** Optional small badge (e.g. flag count) rendered in the nav. */
  badge?: string;
}

export interface SettingsSectionGroup {
  /** Group heading, e.g. "Workspace" or "About". */
  label: string;
  sections: SettingsSectionDescriptor[];
}

interface UseSettingsSectionsOptions {
  experimentalCount: number;
}

/**
 * Builds the section list shown in the modal's left nav. Project- and
 * app-scoped settings (permissions, danger zone, env vars) live on their
 * own `/settings` routes — keeping them out of this global modal stops
 * the nav from changing shape based on the user's current route.
 */
export function useSettingsSections({
  experimentalCount,
}: UseSettingsSectionsOptions): SettingsSectionGroup[] {
  return useMemo(() => [
    {
      label: 'Account',
      sections: [
        { id: 'profile', label: 'Profile', icon: PersonRegular, crumb: 'Your account, active tenant and sign-out' },
      ],
    },
    {
      label: 'Workspace',
      sections: [
        { id: 'general', label: 'General', icon: SettingsRegular, crumb: 'Workspace-wide preferences · saved to your browser' },
        { id: 'appearance', label: 'Appearance', icon: ColorRegular, crumb: 'Theme, canvas orientation and motion' },
        { id: 'accessibility', label: 'Accessibility', icon: AccessibilityRegular, crumb: 'Keyboard shortcuts and motion controls' },
        { id: 'ai', label: 'Copilot chat', icon: CopilotLogoIcon as FluentIcon, crumb: 'Defaults for Copilot in the workflow designer' },
        { id: 'privacy', label: 'Privacy', icon: ShieldRegular, crumb: 'Telemetry and crash reporting' },
        {
          id: 'experimental',
          label: 'Experimental',
          icon: BeakerRegular,
          crumb: 'In-progress features — may change or break',
          badge: String(experimentalCount),
        },
      ],
    },
    {
      label: 'About',
      sections: [
        { id: 'about', label: 'Auto Portal', title: 'About Auto', icon: InfoRegular, crumb: 'Version, build and diagnostics' },
      ],
    },
  ], [experimentalCount]);
}
